import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { TicketDto, TicketSummaryDto } from '@ecom/contracts';
import { errorMessage } from '@/lib/api-client';
import { AsyncSelect } from '@/components/async-select';
import { FilterBar, FilterField, ToggleGroup } from '@/components/filters';
import { staffOptions } from '@/lib/options';
import { can } from '@/lib/session';
import { useAuthStore } from '@/stores/auth.store';
import { EMPTY_TICKET_FILTERS, useSupportStore } from '@/stores/support.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { age, dateTime } from '@/lib/format';
import { CloseIcon, MailIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page } from '@/components/page';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  IconButton,
  Input,
  Pill,
  Select,
  Skeleton,
  Textarea,
  cx,
  type PillTone,
} from '@/components/ui';
import { mediaUrl } from '@/lib/media';

const STATUSES = ['OPEN', 'PENDING_CUSTOMER', 'RESOLVED', 'CLOSED'] as const;
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;

const STATUS_TONES: Record<string, PillTone> = {
  OPEN: 'warning',
  PENDING_CUSTOMER: 'info',
  RESOLVED: 'success',
  CLOSED: 'neutral',
};

/**
 * Support (ADR-028).
 *
 * A queue and a thread. The queue exists so a ticket nobody has picked up is
 * findable — "unassigned" is the filter that matters most on a busy morning —
 * and the thread is where it gets answered, handed to a colleague, or closed.
 *
 * Laid out as an inbox: on a desktop the queue runs down the start side and
 * the open thread fills the rest, each scrolling on its own, so working
 * through the morning's tickets never loses the place in the list. On a phone
 * it is one or the other — the queue, then the thread, and closing the thread
 * goes back to the queue.
 */
export function SupportPage() {
  const {
    items,
    current,
    opening,
    loading,
    loadingMore,
    saving,
    error,
    nextCursor,
    total,
    filters,
    reasons,
    load,
    loadMore,
    loadReasons,
    open,
    close,
    reply,
    update,
    draftReply,
  } = useSupportStore();

  const locale = useUiStore((state) => state.locale);
  const t = useT();

  useEffect(() => {
    void load();
  }, [load, locale]);

  // The reasons are the store's own words, in the active language.
  useEffect(() => {
    void loadReasons();
  }, [loadReasons, locale]);

  // A ticket on its way in takes the thread pane over — on a phone, the screen.
  const threadOpen = current !== null || opening !== null;

  return (
    <Page title={t('supportTickets')} subtitle={t('supportSubtitle')} fill>
      {error ? <Alert>{error}</Alert> : null}

      <div className="flex flex-1 flex-col gap-5 lg:min-h-0 lg:flex-row">
        <ListCard className={cx('lg:w-[24rem] lg:flex-none xl:w-[28rem]', threadOpen && 'max-lg:hidden')}>
          <ListToolbar>
            <FilterBar
              search={filters.search}
              onSearch={(search) => void load({ search })}
              placeholder={t('searchTicketsHint')}
              total={total ?? undefined}
              shown={items.length}
              chips={[
                ...filters.statuses.map((status) => ({
                  key: `s-${status}`,
                  label: t(`ticketStatus${status}` as MessageKey),
                  onRemove: () =>
                    void load({ statuses: filters.statuses.filter((entry) => entry !== status) }),
                })),
                ...(filters.reason
                  ? [
                      {
                        key: 'reason',
                        label: reasons.find((entry) => entry.code === filters.reason)?.label ?? filters.reason,
                        onRemove: () => void load({ reason: '' }),
                      },
                    ]
                  : []),
                ...(filters.unassigned === 'true'
                  ? [{ key: 'unassigned', label: t('unassignedOnly'), onRemove: () => void load({ unassigned: '' }) }]
                  : []),
                ...(filters.from
                  ? [{ key: 'from', label: `${t('filterFrom')}: ${filters.from}`, onRemove: () => void load({ from: '' }) }]
                  : []),
                ...(filters.to
                  ? [{ key: 'to', label: `${t('filterTo')}: ${filters.to}`, onRemove: () => void load({ to: '' }) }]
                  : []),
              ]}
              onClearAll={() => void load(EMPTY_TICKET_FILTERS)}
            >
              {/* One column: the queue is a narrow pane on a desktop. */}
              <div className="grid gap-4">
                <FilterField label={t('filterStatus')}>
                  <ToggleGroup
                    options={STATUSES.map((status) => ({
                      value: status,
                      label: t(`ticketStatus${status}` as MessageKey),
                    }))}
                    selected={filters.statuses}
                    onChange={(statuses) => void load({ statuses })}
                  />
                </FilterField>

                {/* The store's own short list (support settings) — a plain select. */}
                {reasons.length > 0 ? (
                  <FilterField label={t('filterReason')} htmlFor="tk-reason">
                    <Select
                      id="tk-reason"
                      value={filters.reason}
                      onChange={(event) => void load({ reason: event.target.value })}
                    >
                      <option value="">{t('anyValue')}</option>
                      {reasons.map((entry) => (
                        <option key={entry.code} value={entry.code}>
                          {entry.label}
                        </option>
                      ))}
                    </Select>
                  </FilterField>
                ) : null}

                <FilterField label={t('assignedTo')} htmlFor="tk-assignee">
                  <AsyncSelect
                    id="tk-assignee"
                    value={filters.assignedToId}
                    emptyLabel={t('anyAgent')}
                    fetchPage={staffOptions.page}
                    onChange={(id) => void load({ assignedToId: id, unassigned: '' })}
                  />
                </FilterField>

                <FilterField label={t('pickedUp')} htmlFor="tk-unassigned">
                  <Select
                    id="tk-unassigned"
                    value={filters.unassigned}
                    onChange={(event) => void load({ unassigned: event.target.value, assignedToId: '' })}
                  >
                    <option value="">{t('anyState')}</option>
                    <option value="true">{t('unassignedOnly')}</option>
                  </Select>
                </FilterField>

                <div className="grid grid-cols-2 gap-2">
                  <FilterField label={t('filterFrom')} htmlFor="tk-from">
                    <Input
                      id="tk-from"
                      type="date"
                      dir="ltr"
                      value={filters.from}
                      onChange={(event) => void load({ from: event.target.value })}
                    />
                  </FilterField>
                  <FilterField label={t('filterTo')} htmlFor="tk-to">
                    <Input
                      id="tk-to"
                      type="date"
                      dir="ltr"
                      value={filters.to}
                      onChange={(event) => void load({ to: event.target.value })}
                    />
                  </FilterField>
                </div>
              </div>
            </FilterBar>
          </ListToolbar>

          {/* The skeleton only for a queue with nothing in it yet: a filter
              changed beside an open thread dims the list rather than blanking
              it, so the agent keeps their place. */}
          {loading && items.length === 0 ? (
            <div className="flex flex-col gap-3 p-4">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState title={t('noTickets')} body={t('noTicketsHint')} icon={<MailIcon />} />
          ) : (
            <ul
              aria-busy={loading}
              className={cx('min-h-0 overflow-y-auto transition-opacity lg:flex-1', loading && 'opacity-60')}
            >
              {items.map((ticket) => (
                <QueueRow
                  key={ticket.id}
                  ticket={ticket}
                  active={ticket.id === (opening ?? current?.id)}
                  onOpen={() => void open(ticket.id)}
                />
              ))}
            </ul>
          )}

          <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
        </ListCard>

        <Card className={cx('flex min-w-0 flex-1 flex-col lg:min-h-0', !threadOpen && 'max-lg:hidden')}>
          {opening !== null && opening !== current?.id ? (
            <ThreadSkeleton />
          ) : current ? (
            <TicketThread
              // A thread of its own per ticket: a half-written reply must not
              // follow the agent to the next ticket they open.
              key={current.id}
              ticket={current}
              saving={saving}
              onClose={close}
              onReply={(body, isInternal) => reply(current.id, body, isInternal)}
              onUpdate={(patch) => update(current.id, patch)}
              onDraft={() => draftReply(current.id)}
            />
          ) : (
            <EmptyState title={t('supportPickTicket')} icon={<MailIcon />} />
          )}
        </Card>
      </div>
    </Page>
  );
}

/** One ticket in the queue: who, about what, where it stands, and who holds it. */
function QueueRow({ ticket, active, onOpen }: { ticket: TicketSummaryDto; active: boolean; onOpen: () => void }) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  return (
    <li className="border-b border-line">
      <button
        type="button"
        onClick={onOpen}
        aria-current={active || undefined}
        className={cx(
          'flex w-full flex-col gap-1.5 border-s-2 px-5 py-3.5 text-start transition-colors',
          active ? 'border-brand-600 bg-surface-sunken' : 'border-transparent hover:bg-surface-sunken/70',
        )}
      >
        <span className="flex w-full items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium text-ink">{ticket.customerName}</span>
          {/* A guest's answer is also a mail (ADR-041): worth knowing before writing it. */}
          {ticket.guest ? <Pill tone="neutral">{t('ticketGuest')}</Pill> : null}
          {/* Answered on WhatsApp, inside Meta's 24 hours (ADR-107). */}
          {ticket.channel === 'WHATSAPP' ? <Pill tone="success">{t('whatsapp')}</Pill> : null}
          {/* How long since the last word — on an open ticket, how long the
              customer has been waiting on the store. */}
          <time
            dateTime={ticket.lastReplyAt}
            title={dateTime(ticket.lastReplyAt, locale)}
            className={cx(
              'ms-auto shrink-0 whitespace-nowrap text-xs',
              ticket.status === 'OPEN' ? 'font-medium text-warning' : 'text-ink-muted',
            )}
          >
            {age(ticket.lastReplyAt, locale)}
          </time>
        </span>

        <span dir="auto" className="block w-full truncate text-start text-sm text-ink">{ticket.subject}</span>

        <span className="flex w-full min-w-0 items-center gap-2 text-xs text-ink-muted">
          <span dir="ltr" data-numeric className="shrink-0">
            {ticket.number}
          </span>
          {ticket.customerEmail || ticket.whatsappPhone ? (
            <span dir="ltr" className="min-w-0 truncate">
              {ticket.customerEmail ?? ticket.whatsappPhone}
            </span>
          ) : null}
          <span className="ms-auto shrink-0 whitespace-nowrap" title={dateTime(ticket.createdAt, locale)}>
            {t('ticketOpenedAgo', { age: age(ticket.createdAt, locale) })}
          </span>
        </span>

        <span className="flex w-full flex-wrap items-center gap-2">
          <Pill tone={STATUS_TONES[ticket.status] ?? 'neutral'}>{t(`ticketStatus${ticket.status}` as MessageKey)}</Pill>
          {ticket.priority === 'URGENT' || ticket.priority === 'HIGH' ? (
            <Pill tone="danger">{t(`priority${ticket.priority}` as MessageKey)}</Pill>
          ) : null}
          <span className="ms-auto text-xs text-ink-muted">
            {/* Nobody holding it is the state worth seeing at a glance. */}
            {ticket.assignedToName ?? <span className="text-warning">{t('unassigned')}</span>}
          </span>
        </span>
      </button>
    </li>
  );
}

/**
 * The open ticket: who and what at the top, the hand-off under it, the thread
 * scrolling in the middle, and the reply box pinned at the bottom so answering
 * never needs a scroll to the end first.
 */
function TicketThread({
  ticket,
  saving,
  onClose,
  onReply,
  onUpdate,
  onDraft,
}: {
  ticket: TicketDto;
  saving: boolean;
  onClose: () => void;
  onReply: (body: string, isInternal: boolean) => Promise<boolean>;
  onUpdate: (patch: { status?: string; priority?: string; assignedToId?: string | null }) => Promise<boolean>;
  onDraft: () => Promise<string>;
}) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  // Reading the queue is customers:read; answering and handing on are support:manage.
  const mayAnswer = useAuthStore((state) => can(state.user, 'support:manage'));
  const mayOpenOrders = useAuthStore((state) => can(state.user, 'orders:read'));
  const myId = useAuthStore((state) => state.user?.id ?? null);

  const [body, setBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftNote, setDraftNote] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);

  return (
    <section
      aria-label={ticket.subject}
      className="flex min-h-0 flex-1 flex-col"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h2 dir="auto" className="text-start text-base font-semibold text-ink">{ticket.subject}</h2>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            <span dir="ltr" data-numeric>
              {ticket.number}
            </span>
            <span>· {ticket.reasonLabel}</span>
            <span>
              ·{' '}
              {/* The person and the order behind the ticket, a click away. A
                  guest has no account to open. */}
              {ticket.customerId ? (
                <Link to={`/customers/${ticket.customerId}`} className="text-brand-700 hover:underline">
                  {ticket.customerName}
                </Link>
              ) : (
                ticket.customerName
              )}
            </span>
            {ticket.orderNumber ? (
              <span>
                ·{' '}
                {ticket.orderId && mayOpenOrders ? (
                  <Link to={`/orders/${ticket.orderId}`} dir="ltr" data-numeric className="text-brand-700 hover:underline">
                    {ticket.orderNumber}
                  </Link>
                ) : (
                  <span dir="ltr" data-numeric>
                    {ticket.orderNumber}
                  </span>
                )}
              </span>
            ) : null}
          </p>
          {ticket.channel === 'WHATSAPP' ? (
            // Meta's 24 hours (ADR-107): whether a reply goes now, or waits for the customer's next message.
            <p className="mt-1 text-xs text-ink-muted">
              <span dir="ltr" data-numeric className="me-1">
                {ticket.whatsappPhone}
              </span>
              ·{' '}
              {ticket.whatsappWindowUntil
                ? t('whatsappWindowOpen', { time: dateTime(ticket.whatsappWindowUntil, locale) })
                : t('whatsappWindowClosed')}
            </p>
          ) : ticket.guest ? (
            <p className="mt-1 text-xs text-ink-muted">{t('ticketGuestHint')}</p>
          ) : null}
        </div>

        <IconButton label={t('close')} onClick={onClose} className="size-9 shrink-0 border-0">
          <CloseIcon className="size-[18px]" />
        </IconButton>
      </div>

      {/* Handing it on is the point of the screen, so it sits at the top
          rather than under the thread. */}
      <div className="grid gap-3 border-b border-line px-5 py-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="flex items-center justify-between gap-2">
            <label htmlFor="tk-holder">{t('assignedTo')}</label>
            {/* Taking a ticket is the morning's most common move: one click,
                not a search for one's own name. */}
            {mayAnswer && myId && ticket.assignedToId !== myId ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => void onUpdate({ assignedToId: myId })}
                className="font-medium text-brand-700 hover:underline disabled:opacity-50"
              >
                {t('ticketAssignToMe')}
              </button>
            ) : null}
          </span>
          <AsyncSelect
            id="tk-holder"
            value={ticket.assignedToId ?? ''}
            emptyLabel={t('unassigned')}
            fetchPage={staffOptions.page}
            fetchOne={staffOptions.one}
            disabled={saving || !mayAnswer}
            onChange={(id) => void onUpdate({ assignedToId: id || null })}
          />
        </div>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t('status')}
          <Select
            value={ticket.status}
            disabled={saving || !mayAnswer}
            onChange={(event) => void onUpdate({ status: event.target.value })}
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`ticketStatus${status}` as MessageKey)}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t('priority')}
          <Select
            // Always set in the staff copy; null only in a customer's.
            value={ticket.priority ?? 'NORMAL'}
            disabled={saving || !mayAnswer}
            onChange={(event) => void onUpdate({ priority: event.target.value })}
          >
            {PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {t(`priority${priority}` as MessageKey)}
              </option>
            ))}
          </Select>
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {/* Files the customer sent (ADR-044): private, behind links that last ten minutes. */}
        {ticket.attachments.length > 0 ? (
          <div className="mb-4">
            <p className="mb-1.5 text-xs font-medium text-ink">{t('ticketFiles')}</p>
            <div className="flex flex-wrap gap-2">
              {ticket.attachments.map((file) => (
                <a
                  key={file.id}
                  href={mediaUrl(file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center rounded-lg border border-line p-1 text-xs text-ink-muted hover:border-ink"
                >
                  {file.contentType.startsWith('image/') ? (
                    <img src={mediaUrl(file.url)} alt={file.name} className="h-16 w-16 rounded object-cover" />
                  ) : (
                    <span className="px-2 py-1">{file.name}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        ) : null}

        <ul className="flex flex-col gap-3">
          {ticket.messages.map((message) => (
            <li
              key={message.id}
              className={cx(
                'rounded-card border p-3 text-sm',
                message.authorKind === 'SYSTEM'
                  ? 'border-transparent bg-surface-sunken text-xs text-ink-muted'
                  : message.isInternal
                    ? 'border-warning/40 bg-warning/5'
                    : message.authorKind === 'STAFF'
                      ? 'border-brand-600/30 bg-brand-600/5'
                      : 'border-line',
              )}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                <span className="font-medium text-ink">{message.authorName}</span>
                {message.isInternal ? <Pill tone="warning">{t('internalNote')}</Pill> : null}
                {message.pending ? <Pill tone="info">{t('whatsappReplyWaiting')}</Pill> : null}
                <span className="ms-auto">{dateTime(message.createdAt, locale)}</span>
              </div>
              <p dir="auto" className="whitespace-pre-line text-start text-ink">
                {message.body}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t border-line px-5 py-4">
        {!mayAnswer ? (
          <p className="text-sm text-ink-muted">{t('ticketReadOnly')}</p>
        ) : ticket.status === 'CLOSED' ? (
          <p className="text-sm text-ink-muted">{t('ticketClosedNote')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <Textarea
              value={body}
              rows={4}
              aria-label={isInternal ? t('internalNotePlaceholder') : t('replyPlaceholder')}
              placeholder={isInternal ? t('internalNotePlaceholder') : t('replyPlaceholder')}
              onChange={(event) => setBody(event.target.value)}
            />

            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(event) => setIsInternal(event.target.checked)}
                  className="size-4 rounded border-line accent-brand-600"
                />
                {t('internalNote')}
                <span className="text-xs text-ink-muted">({t('internalNoteHint')})</span>
              </label>

              <div className="flex items-center gap-2">
                {/*
                  A draft, into the box the agent was already typing in.
                  Deliberately not a "send" — the assistant writes, a person
                  reads it and presses the button that has always been there.
                */}
                <Button
                  variant="ghost"
                  disabled={saving || drafting}
                  onClick={async () => {
                    setDrafting(true);
                    setDraftError(null);
                    setDraftNote(null);

                    try {
                      setBody(await onDraft());
                      setIsInternal(false);
                      setDraftNote(t('aiDraftInserted'));
                    } catch (cause) {
                      setDraftError(errorMessage(cause, t('ticketDraftFailed')));
                    } finally {
                      setDrafting(false);
                    }
                  }}
                >
                  {drafting ? t('aiDrafting') : t('aiDraft')}
                </Button>

                <Button
                  disabled={saving || body.trim().length === 0}
                  onClick={async () => {
                    const ok = await onReply(body.trim(), isInternal);
                    if (ok) {
                      setBody('');
                      setDraftNote(null);
                    }
                  }}
                >
                  {saving ? t('saving') : isInternal ? t('addNote') : t('sendReply')}
                </Button>
              </div>
            </div>

            {draftNote ? <p className="text-xs text-ink-muted">{draftNote}</p> : null}
            {draftError ? <p className="text-xs text-danger">{draftError}</p> : null}
          </div>
        )}
      </div>
    </section>
  );
}

/** The thread's outline while a ticket is being read — the pane answers the click at once. */
function ThreadSkeleton() {
  const t = useT();

  return (
    <div aria-busy="true" aria-label={t('loading')} className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-2 border-b border-line px-5 py-4">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <div className="grid gap-3 border-b border-line px-5 py-3 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
      <div className="flex flex-col gap-3 px-5 py-4">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    </div>
  );
}
